import React, { useEffect, useState } from "react";
import { use } from "react";
import { useTranslation } from "react-i18next";

// Static business ratios configuration
const BUSINESS_RATIOS = [
  {
    key: "current_ratio",
    translationKey: "currentRatio",
    category: "liquidity",
    min: 0.8,
    max: 3.5,
    decimals: 2,
    suffix: ":1",
  },
  {
    key: "quick_ratio",
    translationKey: "quickRatio",
    category: "liquidity",
    min: 0.5,
    max: 2.8,
    decimals: 2,
    suffix: ":1",
  },
  {
    key: "gross_margin",
    translationKey: "grossMargin",
    category: "profitability",
    min: 15,
    max: 85,
    decimals: 1,
    suffix: "%",
  },
  {
    key: "net_profit_margin",
    translationKey: "netProfitMargin",
    category: "profitability",
    min: 3,
    max: 25,
    decimals: 1,
    suffix: "%",
  },
  {
    key: "debt_to_equity",
    translationKey: "debtToEquityRatio",
    category: "leverage",
    min: 0.1,
    max: 2.5,
    decimals: 2,
    suffix: ":1",
  },
  {
    key: "return_on_assets",
    translationKey: "returnOnAssets",
    category: "profitability",
    min: 2,
    max: 20,
    decimals: 1,
    suffix: "%",
  },
  {
    key: "inventory_turnover",
    translationKey: "inventoryTurnover",
    category: "efficiency",
    min: 2.5,
    max: 12.0,
    decimals: 1,
    suffix: "x",
  },
  {
    key: "asset_turnover",
    translationKey: "assetTurnover",
    category: "efficiency",
    min: 0.5,
    max: 3.2,
    decimals: 2,
    suffix: "x",
  },
];

const BusinessDetailsInfo = ({ businessDetails, progress }) => {
  const { t } = useTranslation();
  const user = JSON.parse(sessionStorage.getItem("userDetails"))?.result;
  // loggedinprofiletype is either "buyerprofile" or "sellerprofile"

  // Permissions
  const canSeeContactInfo =
    user?.loggedinprofiletype === "sellerprofile" ||
    user?.role?.id === 1 ||
    user?.role?.id === 2 ||
    user?.role?.id === 4;
  const canSeeBusinessRatios = canSeeContactInfo;

  const generateBusinessRatios = () => {
    return BUSINESS_RATIOS.map((ratio) => {
      const randomValue = Math.random() * (ratio.max - ratio.min) + ratio.min;
      const formattedValue = randomValue.toFixed(ratio.decimals);

      return {
        ...ratio,
        value: formattedValue,
        displayValue: `${formattedValue}${ratio.suffix}`,
      };
    });
  };
  const [businessRatios, setBusinessRatios] = useState([]);
  useEffect(() => {
    const ratio = generateBusinessRatios();
    setBusinessRatios(ratio);
  }, []);

  return (
    <div className="business-details-container">
      <div className="row">
        <div className="col-lg-6 mb-4">
          <div className="info-card">
            <div className="d-flex align-items-center">
              <div className="dt-icon bgl-info-details">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 5H18V3C18 2.73478 17.8946 2.48043 17.7071 2.29289C17.5196 2.10536 17.2652 2 17 2C16.7348 2 16.4804 2.10536 16.2929 2.29289C16.1054 2.48043 16 2.73478 16 3V5H8V3C8 2.73478 7.89464 2.48043 7.70711 2.29289C7.51957 2.10536 7.26522 2 7 2C6.73478 2 6.48043 2.10536 6.29289 2.29289C6.10536 2.48043 6 2.73478 6 3V5H5C4.20435 5 3.44129 5.31607 2.87868 5.87868C2.31607 6.44129 2 7.20435 2 8V9H22V8C22 7.20435 21.6839 6.44129 21.1213 5.87868C20.5587 5.31607 19.7957 5 19 5Z"
                    fill="white"
                  />
                  <path
                    d="M2 19C2 19.7956 2.31607 20.5587 2.87868 21.1213C3.44129 21.6839 4.20435 22 5 22H19C19.7957 22 20.5587 21.6839 21.1213 21.1213C21.6839 20.5587 22 19.7956 22 19V11H2V19Z"
                    fill="white"
                  />
                </svg>
              </div>
              <div>
                <div className="icon-label">{t("businessStartDate")}</div>
                <p className="icon-value">
                  {new Date(businessDetails?.established_at).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6 mb-4">
          <div className="info-card">
            <div className="d-flex align-items-center">
              <div className="dt-icon bgl-danger-details">
                <svg
                  width="19"
                  height="24"
                  viewBox="0 0 19 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.6601 8.6858C18.5437 8.44064 18.2965 8.28427 18.025 8.28427H10.9728L15.2413 1.06083C15.3697 0.843469 15.3718 0.573844 15.2466 0.354609C15.1214 0.135375 14.8884 -9.37014e-05 14.6359 4.86277e-08L8.61084 0.000656299C8.3608 0.000750049 8.12957 0.1335 8.00362 0.349453L0.0958048 13.905C-0.0310859 14.1224 -0.0319764 14.3911 0.0934142 14.6094C0.218805 14.8277 0.451352 14.9624 0.703117 14.9624H7.71037L5.66943 23.1263C5.58955 23.4457 5.74213 23.7779 6.03651 23.9255C6.13691 23.9757 6.24459 24 6.35123 24C6.55729 24 6.75923 23.9094 6.89638 23.7413L18.5699 9.43186C18.7415 9.22148 18.7766 8.93105 18.6601 8.6858V8.6858Z"
                    fill="white"
                  />
                </svg>
              </div>
              <div>
                <div className="icon-label">{t("businessDeadlineTransac")}</div>
                <p className="icon-value">
                  {new Date(
                    businessDetails?.selling_deadline
                  ).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {canSeeContactInfo ? (
        <div className="row">
          <div className="col-lg-6 mb-4">
            <div className="contact-info">
              <h6>{t("contactInfo")}</h6>
              <div className="contact-item">
                <span className="contact-label">{t("nameInput")}:</span>
                <p className="contact-value">{businessDetails?.contact_name}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("emailInput")}:</span>
                <p className="contact-value">
                  {businessDetails?.contact_email}
                </p>
              </div>
              <div className="contact-item">
                <span className="contact-label">
                  {t("secondaryEmailInput")}:
                </span>
                <p className="contact-value">{businessDetails?.email}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("phoneInput")}:</span>
                <p className="contact-value">{businessDetails?.phone}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">
                  {t("secondaryPhoneInput")}:
                </span>
                <p className="contact-value">{businessDetails?.phone2}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("faxInput")}:</span>
                <p className="contact-value">{businessDetails?.fax}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("currencyInput")}:</span>
                <p className="contact-value">
                  {businessDetails?.currency?.code}
                </p>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="contact-info">
              <h6>{t("locationDetails")}</h6>
              <div className="contact-item">
                <span className="contact-label">{t("addressInput")}:</span>
                <p className="contact-value">{businessDetails?.address}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("cityInput")}:</span>
                <p className="contact-value">{businessDetails?.city}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("countryInput")}:</span>
                <p className="contact-value">{businessDetails?.country}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("regionInput")}:</span>
                <p className="contact-value">{businessDetails?.region}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("postalCodeInput")}:</span>
                <p className="contact-value">{businessDetails?.postal_code}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">{t("descriptionInput")}:</span>
                <p className="contact-value">{businessDetails?.description}</p>
              </div>
              <div className="contact-item">
                <span className="contact-label">
                  {t("businessDetailsCreatedDate")}:
                </span>
                <p className="contact-value">
                  {new Date(businessDetails?.created_at).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="d-flex info-card  py-4 flex-column align-items-center text-center"
          style={{ margin: "30px 0" }}
        >
          <h2 className="fw-bold display-8 mb-1">
            {t("a")} {t(businessDetails?.domain)}
            <span className="text-muted fs-5">
              ({businessDetails?.business_number}-{businessDetails?.id})
            </span>{" "}
            {t("forsalein")}{" "}
            <span className="text-secondary ">{businessDetails?.region}</span>
          </h2>
        </div>
      )}

      <h3 className="section-title">{t("finantialMetrics")}</h3>
      <div className="metrics-container">
        <div className="metric-card">
          <div className="metric-label">{t("businessAskingPrice")}</div>
          <p className="metric-value">
            {businessDetails?.asking_price} {businessDetails?.currency?.symbol}
          </p>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("businessAssetsValue")}</div>
          <p className="metric-value">
            {businessDetails?.assets_value} {businessDetails?.currency?.symbol}
          </p>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("businessEquityOffered")}</div>
          <p className="metric-value">{businessDetails?.equity_offered} %</p>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("businessAverageMonthlySales")}</div>
          <p className="metric-value">
            {businessDetails?.average_monthly_sales}{" "}
            {businessDetails?.currency?.symbol}
          </p>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("businessYearlySales")}</div>
          <p className="metric-value">
            {businessDetails?.yearly_sales} {businessDetails?.currency?.symbol}
          </p>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("businessEbitda")}</div>
          <p className="metric-value">{businessDetails?.ebitda} %</p>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("employeesNumberInput")}</div>
          <p className="metric-value">{businessDetails?.employees_number}</p>
        </div>
      </div>
      {businessDetails?.has_income_statement && canSeeBusinessRatios && (
        <div>
          <h3 className="section-title mt-3">{t("businessRatios")}</h3>
          <div className="metrics-container">
            {businessRatios.map((ratio) => (
              <div key={ratio.key} className="metric-card">
                <div className="metric-label">{t(ratio.translationKey)}</div>
                <p className="metric-value">{ratio.displayValue}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDetailsInfo;
